import { Plus, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { getEffectiveFnsku, printFnskuLabels } from "@/lib/fnskuPrint";
import { labelCounts, storeColor, storeTotals } from "@/lib/boxing";
import BoxTile from "./BoxTile";
import LabelFileRows from "./LabelFileRows";
import StorePlan from "./StorePlan";

// One store: coloured header, its plan, and its boxes as cube tiles.
export default function StoreSection({ store, boxes, flags, editable, canPrint, labels, onAddBox, onOpenBox, onDeleteBox, onMessage }) {
  const { t } = useLang();
  const color = storeColor(store.store_id);
  const { planned, boxed, left } = storeTotals(store);
  const readyCount = boxes.filter(b => b.ready).length;
  const applied = labelCounts(boxes).APPLIED;

  const printAllFnsku = async () => {
    const remaining = store.items.filter(item => item.remaining > 0);
    const printable = remaining.filter(getEffectiveFnsku)
      .map(item => ({ fnsku: getEffectiveFnsku(item), title: `New - ${item.product_name}`, quantity: item.remaining }));
    const skipped = remaining.length - printable.length;
    if (printable.length === 0) {
      onMessage("error", t(skipped > 0 ? "recv.no_printable" : "recv.nothing_to_print"));
      return;
    }
    try {
      const printed = await printFnskuLabels(printable);
      if (skipped > 0) onMessage("error", t("recv.printed_skipped", { count: printed, skipped }));
      else onMessage("success", t("recv.printing", { count: printed }));
    } catch (err) {
      console.error(err);
      onMessage("error", t("recv.print_failed"));
    }
  };

  const showLabel = (box) => labels.stage && (labels.mode || box.label_state !== "NONE");
  const labelled = boxes.filter(b => b.label_state !== "NONE").length;

  return (
    <section className="overflow-hidden rounded-2xl border border-l-8 border-slate-200 bg-white shadow-sm" style={{ borderLeftColor: color }}
      data-testid={`store-section-${store.store_id}`}>
      <header className="flex flex-wrap items-center gap-3.5 border-b border-slate-200 px-4 py-3.5"
        style={{ backgroundColor: `color-mix(in srgb, ${color} 9%, white)` }}>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-xl font-extrabold text-white" style={{ backgroundColor: color }} aria-hidden="true">
          {store.store_name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-44 flex-1">
          <h2 className="break-words text-xl font-extrabold leading-tight tracking-tight text-slate-900">{store.store_name}</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {labels.mode
              ? t("recv.store_summary_labels", { boxes: boxes.length, labelled, applied })
              : t("recv.store_summary", { boxes: boxes.length, ready: readyCount, boxed, planned })}
          </p>
        </div>
        <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ${left === 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {left === 0 ? t("recv.store_all_boxed") : t("recv.left", { count: left })}
        </span>
        {canPrint && (
          <Button size="sm" variant="outline" className="gap-1.5 border-slate-300 text-xs" onClick={printAllFnsku}
            disabled={!store.items.some(item => item.remaining > 0 && getEffectiveFnsku(item))}>
            <Printer className="h-3.5 w-3.5" /> {t("recv.print_all_fnsku")}
          </Button>
        )}
      </header>

      <StorePlan store={store} flags={flags} canPrint={canPrint} onMessage={onMessage} />

      <div className="px-4 pb-4 pt-2">
        <h3 className="mb-2.5 text-sm font-bold text-slate-800">
          {t("recv.boxes_title")} <span className="font-normal text-slate-400">{boxes.length}</span>
        </h3>
        <div className={`grid gap-2 ${labels.mode ? "grid-cols-[repeat(auto-fill,minmax(176px,1fr))]" : "grid-cols-[repeat(auto-fill,minmax(150px,1fr))]"}`}>
          {boxes.map(box => (
            <BoxTile key={box.id} box={box} showStatus={!labels.mode} showLabel={showLabel(box)} onOpen={onOpenBox}
              onDelete={editable && !box.ready ? onDeleteBox : undefined} />
          ))}
          {boxes.length === 0 && (
            <div className="col-span-full rounded-xl border-2 border-dashed border-slate-300 p-3.5 text-center text-sm text-slate-500">
              <b className="block text-slate-900">{t("recv.store_no_boxes")}</b>
              {editable && left > 0 && t("recv.store_no_boxes_hint")}
            </div>
          )}
          {editable && left > 0 && (
            <button type="button" onClick={() => onAddBox(store.store_id)} data-testid={`add-box-${store.store_id}`}
              className="flex min-h-16 items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 font-bold text-slate-600 transition-colors hover:bg-slate-50">
              <Plus className="h-4 w-4" /> {t("recv.add_box")}
            </button>
          )}
        </div>
      </div>

      {labels.stage && <LabelFileRows boxes={boxes} labels={labels} />}
    </section>
  );
}
